import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface ClockTimerContextData {
  isActive: boolean;
  seconds: number;
  minutes: number;
  time: number;
  handleStartTimer: () => void;
  handleStopTimer: () => void;
  resetTimer: () => void;
  handleSetInitialTime: (time: number) => void;
}

interface CountdownProps {
  children: ReactNode;
}

let clockTimeout: NodeJS.Timeout;

export const TimerContext = createContext({} as ClockTimerContextData);

export const TimerProvider = ({ children }: CountdownProps) => {
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(true);

  const minutes = Math.floor(time / 60);
  const seconds = time % 60;

  const handleStartTimer = useCallback(() => {
    setIsActive(true);
  }, []);

  const handleSetInitialTime = useCallback((time: number) => {
    setTime(time);
  }, []);

  const handleStopTimer = useCallback((): void => {
    setIsActive(false);
    clearInterval(clockTimeout);
  }, []);

  useEffect(() => {
    if (isActive) {
      clockTimeout = setInterval(() => {
        setTime((oldValue) => oldValue + 1);
      }, 1000);
    }
  }, [isActive]);

  const resetTimer = useCallback((): void => {
    setIsActive(true);
    setTime(0);
  }, []);

  const value = useMemo(
    () => ({
      isActive,
      seconds,
      minutes,
      time,
    }),
    [isActive, minutes, seconds, time]
  );

  return (
    <TimerContext.Provider
      value={{
        ...value,
        handleStartTimer,
        handleStopTimer,
        resetTimer,
        handleSetInitialTime,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export const useTimerContext = (): ClockTimerContextData => {
  const context = useContext(TimerContext);

  if (!context) {
    throw new Error('There was an error trying to use useTimerContext');
  }

  return context;
};
